import { GovernmentIdAttribute } from 'dashboard/models/verification-template-version';

export enum AttributeComparisonOperator {
  DateSimilarity = 'date_similarity',
  DateDifferenceDay = 'date_difference_day',
  DateDifferenceMonth = 'date_difference_month',
  DateDifferenceYear = 'date_difference_year',
  DoingBusinessAs = 'doing_business_as',
  HaversineDistance = 'haversine_distance',
  Nickname = 'nickname',
  StringDifference = 'string_difference',
  StringSimilarity = 'string_similarity',
  Substring = 'substring',
  Tokenization = 'tokenization',
  // deprecated
  JaroWinklerSimilarity = 'jaro_winkler_similarity',
  DamerauLevenshteinSimilarity = 'damerau_levenshtein_similarity',
  StringDifferencePrefix = 'string_difference_prefix',
  StringDifferenceSimilarity = 'string_difference_suffix',
}

export enum AttributeComparisonResultStatus {
  Passed = 'passed',
  Failed = 'failed',
  NotApplicable = 'not_applicable',
}

export interface AttributeComparisonResult {
  'comparison-type'?: AttributeComparisonOperator;
  status?: AttributeComparisonResultStatus;
}

export interface AttributeValue {
  'normalized-extracted-attribute'?: string;
  'normalized-claimed-attribute'?: string;
}

export interface AttributeComparison {
  'attribute-description'?: string;
  'attribute-keys'?: GovernmentIdAttribute[];
  'attribute-values'?: AttributeValue[];
  'attribute-comparison-results'?: AttributeComparisonResult[];
}

export interface ComparisonInputResult {
  slug?: string;
  'attribute-comparisons'?: AttributeComparison[];
}

export const ComparisonNameMap: Record<string, string> = {
  address: 'Address',
  acronym: 'Acronym',
  date_difference_day: 'Day Difference',
  date_difference_month: 'Month Difference',
  date_difference_year: 'Year Difference',
  date_similarity: 'Date Similarity',
  doing_business_as: 'Doing Business As',
  haversine_distance: 'Haversine Distance',
  nickname: 'Nickname Matching',
  regex_match: 'Regex Matching',
  soundex: 'Soundex',
  string_difference: 'String Difference',
  string_difference_first_token: 'String Difference First Token',
  string_difference_last_token: 'String Difference Last Token',
  string_difference_prefix: 'String Difference Prefix',
  string_difference_suffix: 'String Difference Suffix',
  string_missing: 'String Missing',
  string_not_equal: 'String Not Equal',
  string_similarity: 'String Similarity',
  substring: 'Substring Matching',
  tokenization: 'Tokenization Matching',
  tokenization_prefix: 'Tokenization Prefix Matching',
  url_domain: 'URL Domain',
  url_ip_address: 'URL IP Address',
  // deprecated
  jaro_winkler_similarity: 'Jaro Winkler Similarity',
  damerau_levenshtein_similarity: 'Damerau Levenshtein Similarity',
};

export const ComparisonDescriptionMap: Record<string, string> = {
  address:
    'Determines if the two country-specific address fields have the same address\ne.g. “123 Main St” and “123 Main Street” are a match',
  acronym:
    'Determines if two attributes are acronyms of each other\ne.g. ”SAP” is an acronym of “Salt and Pepper”',
  date_difference_day: 'Determines partial matching based on number of days between two dates',
  date_difference_month: 'Determines partial matching based on number of months between two dates',
  date_difference_year: 'Determines partial matching based on number of years between two dates',
  date_similarity:
    'Determines partial matching based on differences between the day, month and year',
  doing_business_as: 'Splits the string by DBA and compares against both names individually',
  haversine_distance:
    'Determines partial matching based on distance between two address geocoordinates',
  nickname:
    'Detects if one attribute is a nickname of the other attribute\ne.g. “Jim“ is a nickname of “James“',
  regex_match:
    'Determines if one attribute is a regex of another\ne.g. ^[FT]ree$ is a match for Free and Tree',
  soundex: 'Determines if one attribute generates the same soundex code as the other attribute',
  string_difference:
    'Determines partial matching based on character differences\ne.g. John and Jon have a difference of 1',
  string_difference_first_token:
    'Determines partial matching based on the first token\'s character differences\ne.g. "John Doe" and "Jon Smith" have a difference of 1',
  string_difference_last_token:
    'Determines partial matching based on the last token\'s character differences\ne.g. "Donald Duck" and "Daffy Dock" have a difference of 1',
  string_difference_prefix:
    'Determines partial matching based on character differences\ne.g. John and Jon have a difference of 1',
  string_difference_suffix:
    'Determines partial matching based on character differences\ne.g. John and Jon have a difference of 1',
  string_missing: 'Detects if one attribute is an empty string or null',
  string_not_equal: 'Detects if two attributes do not match \ne.g. “Jim“ does not match “Paul“',
  string_similarity: 'Determines partial matching based on algorithms like Jaro-Winkler similarity',
  substring:
    'Detects if one attribute is a substring of the other attribute\ne.g. “Apple” is a substring of “Pineapple”',
  tokenization:
    'Detects if one attribute’s token matches another attribute’s tokens\ne.g. “John Doe” has a token match with “Jane Doe”',
  tokenization_prefix:
    'Detects if one attribute’s first N token matches another attribute’s first N tokens\ne.g. "Johnny Apple Smith” has two matching prefixed tokens with “Jonny Apple Doe”',
  url_domain:
    'Determines if two URLs have the same domain\ne.g ”withpersona.com” and ”withpersona.net” have the same second level domain ”withpersona”',
  url_ip_address:
    'Detects if two URLs map to a commmon IP address\ne.g ”withpersona.com” and ”withpersona.com/blog” maps to the common IP address',
  // deprecated
  jaro_winkler_similarity:
    'Determines partial matching based on algorithms like Jaro-Winkler similarity',
  damerau_levenshtein_similarity:
    'Determines partial matching based on algorithms like Damerau-Levenshtein similarity',
};
